import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import {
  CartUpdateEvent,
  ThemeEvents,
  VariantSelectedEvent,
} from "@theme/events";
import {
  DialogComponent,
  DialogCloseEvent,
  DialogOpenEvent,
} from "@theme/dialog";
import { mediaQueryLarge, getIOSVersion } from "@theme/utilities";
import VariantPicker from "@theme/variant-picker";

/**
 * True when no option value is purchasable (e.g. every variant OOS). Used in quick-add where
 * `data-product-available` may be wrong off-PDP (it was previously synced from `.pdp` only).
 * @param {Element} picker
 */
function quickAddPickerHasNoPurchasableOption(picker) {
  const radios = picker.querySelectorAll('input[type="radio"]');
  if (!radios.length) return false;
  return !Array.from(radios).some(
    (r) =>
      /** @type {HTMLInputElement} */ (r).dataset.optionAvailable === "true",
  );
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** Per-URL size-guide source (innerHTML of the PDP's <template data-size-guide-source>). */
  /** @type {Map<string, string>} */
  #cachedSizeGuide = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController;
  /** @type {((event: Event) => void) | null} */
  #variantUpdateListener = null;
  /** @type {HTMLElement | null} */
  #previousActiveElement = null;
  /** @type {(() => void) | null} */
  #quickAddSubmitUpdater = null;

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest("product-card")
      );
    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest("product-hotspot-component")
      );
    // `<product-card>` is upgraded by `src/product-card.js`, which is dynamically
    // imported from `app.js`. If the user clicks quick-add before that import has
    // resolved, the element is in the DOM but hasn't been upgraded yet, so the
    // class method `getProductCardLink()` doesn't exist. Fall back to the same
    // query the method performs internally so we never throw.
    const productCardLink =
      typeof productCard?.getProductCardLink === "function"
        ? productCard.getProductCardLink()
        : productCard?.querySelector('[ref="productCardLink"]') || null;
    const hotspotLink =
      typeof hotspotProduct?.getHotspotProductLink === "function"
        ? hotspotProduct.getHotspotProductLink()
        : null;
    const productLink = productCardLink || hotspotLink;

    if (!productLink?.href) return "";

    const url = new URL(productLink.href);

    if (url.searchParams.has("variant")) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set("variant", selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest("product-card")
      );
    // Same upgrade-race guard as in `productPageUrl`.
    if (typeof productCard?.getSelectedVariantId === "function") {
      return productCard.getSelectedVariantId() || null;
    }
    const input = /** @type {HTMLInputElement | null} */ (
      productCard?.querySelector('input[ref="variantId"]') || null
    );
    return input?.value || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#cartUpdateAbortController = new AbortController();
    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState,
    );
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#cachedSizeGuide.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const optionsContainer = document.getElementById("quick-add-modal-options");
    if (!optionsContainer) return;
    const variantPicker = /** @type {VariantPicker | null} */ (
      optionsContainer.querySelector("variant-picker")
    );
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
    // Cached PDP markup restores data-current-checked; modal CSS treats that as selected even when :checked is false.
    for (const radio of variantPicker.querySelectorAll('input[type="radio"]')) {
      const input = /** @type {HTMLInputElement} */ (radio);
      input.checked = false;
      input.removeAttribute("checked");
      input.dataset.currentChecked = "false";
      input.dataset.previousChecked = "false";
    }

    const dialog = document.getElementById("quick-add-dialog");
    if (dialog) {
      dialog.setAttribute(
        "data-product-available",
        quickAddPickerHasNoPurchasableOption(variantPicker) ? "false" : "true",
      );
    }
    this.#quickAddSubmitUpdater?.();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);
    const needsFetch = !productGrid;

    if (needsFetch) this.toggleAttribute("loading", true);

    try {
      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector("[data-product-grid-content]");
          if (gridElement) {
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
          // Pluck the product's size-guide chart from the fetched PDP. The
          // global drawer (layout/theme.liquid) is empty until something stamps
          // it; openSizeGuide() below feeds this innerHTML in.
          const sgTemplate = /** @type {HTMLTemplateElement | null} */ (
            html.querySelector("template[data-size-guide-source]")
          );
          this.#cachedSizeGuide.set(
            currentUrl,
            sgTemplate ? sgTemplate.innerHTML : "",
          );
        }
      }

      if (productGrid) {
        const freshContent = /** @type {Element} */ (
          productGrid.cloneNode(true)
        );
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
        this.#clearModalVariantSelection();
      }

      this.#openQuickAddModal();
    } finally {
      if (needsFetch) this.removeAttribute("loading");
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute("stay-visible", false);
        this.#previousActiveElement?.focus();
        this.#previousActiveElement = null;
      },
      { once: true },
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#previousActiveElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    // Hand the dialog this product's size-guide chart so its "Find your size"
    // button can stamp it into the global drawer host on demand. Empty string
    // means the product has no chart — in that case openSizeGuide() falls back
    // to the page-level <template> if any (e.g. user is on a PDP).
    dialogComponent.setSizeGuideHTML(
      this.#cachedSizeGuide.get(this.productPageUrl) || "",
    );
    dialogComponent.setAccessibleName(this.dataset.productTitle || "");

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, "text/html");
    } catch (error) {
      if (error.name === "AbortError") return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Builds a clean quickshop layout from the fetched product grid content.
   * @param {Element} productGrid
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById("quick-add-modal-content");
    if (!productGrid || !modalContent) return;

    const firstImg = productGrid.querySelector(
      ".product-information__media img",
    );
    const variantPicker = productGrid.querySelector("variant-picker");
    const productFormComponent = productGrid.querySelector(
      "product-form-component",
    );
    const priceEl = productGrid.querySelector("product-price");

    const qaDialogEl = document.getElementById("quick-add-dialog");
    if (qaDialogEl) {
      const pdpRoot = productGrid.closest(".pdp");
      const pdpSaysUnavailable =
        pdpRoot?.getAttribute("data-product-available") === "false";
      const noPurchasableVariantOption =
        variantPicker instanceof HTMLElement &&
        quickAddPickerHasNoPurchasableOption(variantPicker);
      qaDialogEl.setAttribute(
        "data-product-available",
        pdpSaysUnavailable || noPurchasableVariantOption ? "false" : "true",
      );
    }

    // -- Product card --
    const card = document.createElement("div");
    card.className = "quick-add-modal__card";

    if (firstImg) {
      const img = /** @type {HTMLImageElement} */ (firstImg.cloneNode(false));
      img.className = "quick-add-modal__image";
      img.removeAttribute("style");
      img.removeAttribute("loading");
      card.appendChild(img);
    }

    const info = document.createElement("div");
    info.className = "quick-add-modal__info";

    const titleEl = document.createElement("a");
    titleEl.href = this.productPageUrl;
    titleEl.className = "quick-add-modal__title";
    titleEl.textContent = this.dataset.productTitle || "";
    info.appendChild(titleEl);

    if (priceEl) info.appendChild(priceEl);
    card.appendChild(info);

    // -- Variant picker: uncheck all + clear data-current-checked (modal CSS uses it for “selected” look) --
    if (variantPicker) {
      for (const radio of variantPicker.querySelectorAll(
        'input[type="radio"]',
      )) {
        const input = /** @type {HTMLInputElement} */ (radio);
        input.checked = false;
        input.removeAttribute("checked");
        input.dataset.currentChecked = "false";
        input.dataset.previousChecked = "false";
      }
    }

    // -- Form: simplify the button --
    if (productFormComponent) {
      for (const el of productFormComponent.querySelectorAll(
        ".quantity-selector, .quantity-label, shopify-accelerated-checkout, shopify-accelerated-checkout-cart",
      )) {
        el.remove();
      }

      const btn = /** @type {HTMLButtonElement | null} */ (
        productFormComponent.querySelector('button[name="add"]')
      );
      if (btn) {
        if (variantPicker) {
          const dialogEl = document.getElementById("quick-add-dialog");
          const selectSizeLabel =
            dialogEl?.getAttribute("data-select-size-label") || "Select size";
          const soldOutLabel =
            dialogEl?.getAttribute("data-sold-out-label") || "Sold out";
          const fullyUnavailable =
            dialogEl?.getAttribute("data-product-available") === "false";
          btn.textContent = fullyUnavailable ? soldOutLabel : selectSizeLabel;
          btn.className = "quick-add-modal__submit";
          btn.disabled = true;
          btn.setAttribute("aria-disabled", "true");
        } else {
          const soldOutLabel =
            document
              .getElementById("quick-add-dialog")
              ?.getAttribute("data-sold-out-label") || "Sold out";
          btn.classList.add("quick-add-modal__submit");
          const variantOk = !btn.disabled;
          if (variantOk) {
            btn.disabled = false;
            btn.removeAttribute("aria-disabled");
          } else {
            btn.disabled = true;
            btn.setAttribute("aria-disabled", "true");
            btn.textContent = soldOutLabel;
          }
        }
      }
    }

    // -- Assemble: card in content, variant picker + form in options (Find your size sits between them in the DOM) --
    morph(modalContent, card);

    const optionsContainer = document.getElementById("quick-add-modal-options");
    if (optionsContainer) {
      const optionsWrapper = document.createElement("div");
      if (variantPicker) optionsWrapper.appendChild(variantPicker);
      if (productFormComponent)
        optionsWrapper.appendChild(productFormComponent);
      morph(optionsContainer, optionsWrapper);
    }

    // -- Wire up variant → button state --
    this.#bindVariantButtonState();
  }

  /**
   * Clears variant selection in the modal after updateVariantPicker so no size looks pre-selected.
   */
  #clearModalVariantSelection() {
    const optionsContainer = document.getElementById("quick-add-modal-options");
    if (!optionsContainer) return;

    const picker = optionsContainer.querySelector("variant-picker");
    if (picker) {
      for (const radio of picker.querySelectorAll('input[type="radio"]')) {
        const input = /** @type {HTMLInputElement} */ (radio);
        input.checked = false;
        input.removeAttribute("checked");
        input.dataset.currentChecked = "false";
        input.dataset.previousChecked = "false";
      }

      const btn = /** @type {HTMLButtonElement | null} */ (
        optionsContainer.querySelector(".quick-add-modal__submit")
      );
      if (btn) {
        const dialog = document.getElementById("quick-add-dialog");
        const selectSizeLabel =
          dialog?.getAttribute("data-select-size-label") || "Select size";
        const soldOutLabel =
          dialog?.getAttribute("data-sold-out-label") || "Sold out";
        const fullyUnavailable =
          dialog?.getAttribute("data-product-available") === "false" ||
          quickAddPickerHasNoPurchasableOption(picker);
        btn.textContent = fullyUnavailable ? soldOutLabel : selectSizeLabel;
        btn.disabled = true;
        btn.setAttribute("aria-disabled", "true");
      }
    }
  }

  /**
   * When a size is picked: enable + add label if in stock; disabled + sold out if not.
   * Notify-me flow temporarily removed (reinstate notify label + submit handler below).
   */
  #bindVariantButtonState() {
    this.#quickAddSubmitUpdater = null;
    const optionsContainer = document.getElementById("quick-add-modal-options");
    if (!optionsContainer) return;
    const picker = optionsContainer.querySelector("variant-picker");
    const btn = /** @type {HTMLButtonElement | null} */ (
      optionsContainer.querySelector(".quick-add-modal__submit")
    );
    const form = optionsContainer.querySelector(
      'form[data-type="add-to-cart-form"]',
    );
    if (!picker || !btn) return;

    const dialogEl = document.getElementById("quick-add-dialog");
    const addLabel =
      dialogEl?.getAttribute("data-add-to-cart-label") || "Add to cart";
    const soldOutLabel =
      dialogEl?.getAttribute("data-sold-out-label") || "Sold out";
    const selectSizeLabel =
      dialogEl?.getAttribute("data-select-size-label") || "Select size";

    let awaitingVariantUpdate = false;

    const updateButtonFromSelection = () => {
      if (dialogEl?.getAttribute("data-product-available") === "false") {
        btn.textContent = soldOutLabel;
        btn.disabled = true;
        btn.setAttribute("aria-disabled", "true");
        return;
      }

      const checked = /** @type {HTMLInputElement | null} */ (
        picker.querySelector('input[type="radio"]:checked')
      );
      if (!checked) {
        if (quickAddPickerHasNoPurchasableOption(picker)) {
          btn.textContent = soldOutLabel;
        } else {
          btn.textContent = selectSizeLabel;
        }
        btn.disabled = true;
        btn.setAttribute("aria-disabled", "true");
        return;
      }
      const available = checked.dataset.optionAvailable !== "false";
      btn.textContent = available ? addLabel : soldOutLabel;
      if (awaitingVariantUpdate) {
        btn.disabled = true;
        btn.setAttribute("aria-disabled", "true");
      } else {
        btn.disabled = !available;
        btn.setAttribute("aria-disabled", available ? "false" : "true");
      }
    };

    this.#quickAddSubmitUpdater = updateButtonFromSelection;
    picker.addEventListener("change", () => {
      awaitingVariantUpdate = true;
      updateButtonFromSelection();
    });

    // Re-apply after variantUpdate (product-form may morph the button). Run synchronously to avoid flicker.
    if (this.#variantUpdateListener) {
      document.removeEventListener(
        ThemeEvents.variantUpdate,
        this.#variantUpdateListener,
      );
    }
    const variantUpdateHandler = (/** @type {Event} */ e) => {
      if (
        !e.target ||
        !optionsContainer.contains(/** @type {Node} */ (e.target))
      )
        return;
      awaitingVariantUpdate = false;
      updateButtonFromSelection();
    };
    this.#variantUpdateListener = variantUpdateHandler;
    document.addEventListener(ThemeEvents.variantUpdate, variantUpdateHandler);

    updateButtonFromSelection();

    if (form) {
      const addingLabel =
        dialogEl?.getAttribute("data-adding-label") || "Adding...";
      form.addEventListener("submit", (e) => {
        const variantInput = /** @type {HTMLInputElement | null} */ (
          form.querySelector('input[name="id"]')
        );
        if (!variantInput?.value) {
          e.preventDefault();
          return;
        }
        const checked = /** @type {HTMLInputElement | null} */ (
          picker.querySelector('input[type="radio"]:checked')
        );
        if (checked && checked.dataset.optionAvailable === "false") {
          e.preventDefault();
          return;
        }
        // Notify me (reinstate): on OOS submit, close dialog and dispatch notify-me-open.
        btn.textContent = addingLabel;
        btn.setAttribute("aria-busy", "true");
      });
    }
  }

  /** @param {VariantSelectedEvent} event */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest("product-card") !== this.closest("product-card"))
      return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === "1" ? "add" : "choose";
    this.setAttribute("data-quick-add-button", quickAddButton);
  };
}

if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {((e: KeyboardEvent) => void) | null} */
  #focusTrapKeyHandler = null;
  /** Chart HTML for the product currently shown in the modal. Set by QuickAddComponent
   *  before showDialog(), so openSizeGuide() can stamp it into the global drawer host. */
  /** @type {string} */
  #sizeGuideHTML = "";

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink,
    );
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /** @param {string} html */
  setSizeGuideHTML(html) {
    this.#sizeGuideHTML = typeof html === "string" ? html : "";
  }

  /**
   * Names the dialog after the product it was opened for. Called by
   * QuickAddComponent before showDialog().
   * @param {string} title
   */
  setAccessibleName(title) {
    const dialog = this.refs?.dialog;
    if (dialog instanceof HTMLElement && title) {
      dialog.setAttribute("aria-label", title);
    }
  }

  /**
   * "Find your size" handler. Bound declaratively from the modal markup via
   * `on:click="/openSizeGuide"` (Component framework, see component.js).
   *
   * Keeps this <dialog> open. The size-guide drawer (snippets/size-guide-drawer.liquid)
   * is itself a <dialog> opened with showModal(); both end up on the browser
   * top layer, with the most recently opened dialog (the size guide) stacking
   * above quick-add. When the user closes the size guide, focus returns here
   * automatically and the cart flow continues uninterrupted.
   *
   * The product's chart HTML was cached during fetch by QuickAddComponent (see
   * #cachedSizeGuide) and handed to us via setSizeGuideHTML() before showDialog().
   * Empty string → openSizeGuide() falls back to the page's
   * <template data-size-guide-source> if any (PDP), otherwise the host stays
   * empty and the drawer just shows the heading (acceptable on collection grids
   * where the underlying page has no size-guide source).
   */
  openSizeGuide() {
    if (typeof window.openSizeGuide === "function") {
      window.openSizeGuide(this.#sizeGuideHTML);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    this.#removeFocusTrap();
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
    );
  }

  #handleDialogOpen = () => {
    this.#setupFocusTrap();
  };

  showDialog() {
    super.showDialog();
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, {
      once: true,
    });
  }

  #getFocusables() {
    const dialog = this.refs?.dialog;
    if (!dialog) return [];
    return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el instanceof HTMLElement && el.offsetParent !== null,
    );
  }

  #setupFocusTrap() {
    this.#removeFocusTrap();
    const dialog = this.refs?.dialog;
    if (dialog instanceof HTMLElement) dialog.focus({ preventScroll: true });

    const handler = (/** @type {KeyboardEvent} */ e) => {
      if (e.key !== "Tab") return;
      const list = this.#getFocusables();
      if (list.length === 0) return;
      const i = list.indexOf(/** @type {HTMLElement} */ (e.target));
      const next =
        i === -1
          ? e.shiftKey
            ? list[list.length - 1]
            : list[0]
          : e.shiftKey
            ? i <= 0
              ? list[list.length - 1]
              : list[i - 1]
            : i >= list.length - 1
              ? list[0]
              : list[i + 1];
      if (next instanceof HTMLElement) {
        e.preventDefault();
        next.focus();
      }
    };
    this.#focusTrapKeyHandler = handler;
    this.addEventListener("keydown", handler);
  }

  #removeFocusTrap() {
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    if (this.#focusTrapKeyHandler) {
      this.removeEventListener("keydown", this.#focusTrapKeyHandler);
      this.#focusTrapKeyHandler = null;
    }
  }

  /** @param {CartUpdateEvent} event */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) {
      const btn = this.querySelector(".quick-add-modal__submit");
      if (btn instanceof HTMLElement) {
        btn.textContent =
          this.getAttribute("data-add-to-cart-label") || "Add to cart";
        btn.removeAttribute("aria-busy");
      }
      return;
    }
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const titleLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".quick-add-modal__title")
    );

    if (anchorElement && titleLink) {
      titleLink.href = anchorElement.href;
    }
  };

  #handleDialogClose = () => {
    this.#removeFocusTrap();
    const iosVersion = getIOSVersion();
    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (iosVersion.major === 16 && iosVersion.minor >= 4)
    )
      return;

    requestAnimationFrame(() => {
      const grid = /** @type {HTMLElement | null} */ (
        document.querySelector("#ResultsList [product-grid-view]")
      );
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = "";
        });
      }
    });
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
